/**
 * Filter option row presentation for the mobile filter action sheet.
 *
 * Renders a single Pressable row with a label and a Check icon when
 * the option is selected. Mirrors the visual style of the sort sheet
 * option items for consistency.
 * @module features/scouting/components/MobileFilterSheet/MobileFilterOption/MobileFilterOptionView
 */

import { Check } from "lucide-react-native";
import React, { memo } from "react";

import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";

/** Props for MobileFilterOptionView */
interface MobileFilterOptionViewProps {
  /** Display label for the filter option */
  readonly label: string;
  /** Whether this option is currently selected */
  readonly isSelected: boolean;
  /** Callback when the option row is pressed */
  readonly onPress: () => void;
}

/**
 * Renders a single filter option row with a check icon indicator.
 * @param root0 - Filter option display data and press handler
 * @param root0.label - Filter option label
 * @param root0.isSelected - Whether this option is selected
 * @param root0.onPress - Press handler
 */
const MobileFilterOptionView = ({
  label,
  isSelected,
  onPress,
}: MobileFilterOptionViewProps) => (
  <Pressable
    onPress={onPress}
    className="flex-row items-center justify-between px-1 py-3"
  >
    <Text
      variant="unstyled"
      className={`text-title-sm ${isSelected ? "font-heading text-content-primary" : "text-content-subtle"}`}
    >
      {label}
    </Text>
    {isSelected ? (
      <Icon as={Check} size="sm" className="text-content-primary" />
    ) : null}
  </Pressable>
);

MobileFilterOptionView.displayName = "MobileFilterOptionView";

export default memo(MobileFilterOptionView);
